import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle, Clock, User as UserIcon, MapPin, Wrench, XCircle,
  RefreshCw, PlayCircle, CheckCircle2, ArrowRight, Loader2, Plus,
} from "lucide-react";

const URGENCY_STYLE = {
  low:      { badge: "bg-slate-100 text-slate-700 border-slate-300", stripe: "border-l-slate-400" },
  medium:   { badge: "bg-amber-50 text-amber-800 border-amber-300",   stripe: "border-l-amber-400" },
  high:     { badge: "bg-orange-50 text-orange-800 border-orange-300", stripe: "border-l-orange-500" },
  critical: { badge: "bg-red-50 text-red-800 border-red-300",           stripe: "border-l-red-600" },
};

const ROOT_CAUSES = [
  "Electrical", "Mechanical", "Hydraulic/Pneumatic", "Operator Error",
  "Wear-and-Tear", "Consumable", "External Power", "Software/Control", "Other",
];

function formatAge(iso) {
  if (!iso) return "—";
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
}

function slaCountdown(iso) {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  const overdue = diffMs < 0;
  const mins = Math.abs(Math.floor(diffMs / 60000));
  const label =
    mins < 60 ? `${mins}m` :
    mins < 1440 ? `${Math.floor(mins / 60)}h ${mins % 60}m` :
    `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
  return { overdue, label };
}

function BreakdownCard({ req, onOpen }) {
  const style = URGENCY_STYLE[req.urgency] || URGENCY_STYLE.medium;
  const age = formatAge(req.reported_at);
  const sla = slaCountdown(req.sla_resolve_by);
  return (
    <button
      onClick={() => onOpen(req)}
      className={`w-full text-left bg-white border border-slate-200 hover:border-slate-400 shadow-sm hover:shadow-md rounded-lg p-3 border-l-4 ${style.stripe} transition`}
      data-testid={`kanban-card-${req.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${style.badge}`}>
          {req.urgency}
        </span>
        <span className="text-[10px] text-slate-500 font-mono">{age} ago</span>
      </div>
      <div className="text-sm font-semibold text-slate-900 line-clamp-2 mb-1">{req.asset_name}</div>
      <div className="text-[11px] text-slate-500 font-mono mb-2">{req.asset_code}</div>
      <p className="text-xs text-slate-700 line-clamp-2 mb-2">{req.description}</p>
      <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
        {req.asset_location && (
          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{req.asset_location}</span>
        )}
        {req.assigned_to_name && (
          <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" />{req.assigned_to_name}</span>
        )}
        {sla && req.status !== "completed" && (
          <span className={`inline-flex items-center gap-1 ml-auto font-semibold ${sla.overdue ? "text-red-600" : "text-slate-600"}`}>
            <Clock className="w-3 h-3" />
            {sla.overdue ? `Overdue ${sla.label}` : `SLA ${sla.label}`}
          </span>
        )}
      </div>
    </button>
  );
}

export default function MaintenanceKanban() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canReport = hasCapability(user, "report_breakdown");
  const canAssign = hasCapability(user, "assign_breakdown");
  const canCancel = hasCapability(user, "cancel_breakdown");

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Completion form
  const [resolution, setResolution] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [cost, setCost] = useState("0");
  // Cancel form
  const [cancelReason, setCancelReason] = useState("");

  const refresh = () => {
    setLoading(true);
    api.get("/maintenance/kanban")
      .then((r) => setBoard(r.data))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load Kanban"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    const int = setInterval(refresh, 30000);
    return () => clearInterval(int);
  }, []);

  useEffect(() => {
    // Load technicians for assignment dropdown. Use /users/technicians which is
    // accessible to maintenance_manager; /users would 403 for that role.
    if (!canAssign) return;
    api.get("/users/technicians").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.users || []);
      // Backend accepts only technician or maintenance_manager as assignee
      setTechnicians(list.filter((u) =>
        ["technician", "maintenance_manager"].includes(u.role) && !u.is_suspended
      ));
    }).catch(() => {});
  }, [canAssign]);

  const closeDialog = () => {
    setSelected(null);
    setAssignTo("");
    setResolution("");
    setRootCause("");
    setCost("0");
    setCancelReason("");
  };

  const doAssign = async () => {
    if (!assignTo) return toast.error("Pick a technician");
    setActionLoading(true);
    try {
      await api.patch(`/maintenance-requests/${selected.id}/assign`, { assigned_to: assignTo });
      toast.success("Assigned — notification email sent");
      closeDialog();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Assign failed");
    } finally { setActionLoading(false); }
  };

  const doStart = async () => {
    setActionLoading(true);
    try {
      await api.patch(`/maintenance-requests/${selected.id}/start`, {});
      toast.success("Work started");
      closeDialog();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Start failed");
    } finally { setActionLoading(false); }
  };

  const doComplete = async () => {
    if (!resolution.trim()) return toast.error("Describe the resolution");
    if (!rootCause) return toast.error("Pick a root cause");
    setActionLoading(true);
    try {
      await api.patch(`/maintenance-requests/${selected.id}/complete`, {
        resolution: resolution.trim(),
        root_cause_code: rootCause,
        cost: parseFloat(cost || 0),
      });
      toast.success("Breakdown resolved");
      closeDialog();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Complete failed");
    } finally { setActionLoading(false); }
  };

  const doCancel = async () => {
    if (!cancelReason.trim()) return toast.error("Reason is required");
    setActionLoading(true);
    try {
      await api.patch(`/maintenance-requests/${selected.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Request cancelled");
      closeDialog();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Cancel failed");
    } finally { setActionLoading(false); }
  };

  const isAssignee = selected && (selected.assigned_to === user?.id);
  const canStart = selected?.status === "assigned" && (isAssignee || ["admin", "admin_addl", "manager", "admin_manager", "maintenance_manager"].includes(user?.role));
  const canDoComplete = selected && ["assigned", "in_progress"].includes(selected.status) && (isAssignee || ["admin", "admin_addl", "manager", "admin_manager", "maintenance_manager"].includes(user?.role));

  const columns = board?.columns
    ? [
        { key: "requested",   ...board.columns.requested   },
        { key: "assigned",    ...board.columns.assigned    },
        { key: "in_progress", ...board.columns.in_progress },
        { key: "completed",   ...board.columns.completed   },
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="maintenance-kanban-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Breakdown Kanban</h1>
          <p className="text-sm text-slate-500 mt-1">
            Live board of all breakdown requests. Auto-refreshes every 30s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="kanban-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          {canReport && (
            <Button size="sm" onClick={() => navigate("/breakdown-report")} data-testid="kanban-new-report-btn">
              <Plus className="w-4 h-4 mr-2" /> Report Breakdown
            </Button>
          )}
        </div>
      </div>

      {loading && !board ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading board…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {columns.map((col) => (
            <div key={col.key} className="bg-slate-50 rounded-lg p-3 min-h-[400px]" data-testid={`kanban-col-${col.key}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-sm font-semibold text-slate-700">{col.title}</div>
                <span className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-600">
                  {col.count}
                </span>
              </div>
              <div className="space-y-2">
                {col.items.length === 0 ? (
                  <div className="text-xs text-slate-400 italic text-center py-8">No items</div>
                ) : (
                  col.items.map((req) => (
                    <BreakdownCard key={req.id} req={req} onOpen={setSelected} />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl" data-testid="kanban-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  {selected.asset_name}
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${URGENCY_STYLE[selected.urgency]?.badge}`}>
                    {selected.urgency}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  <span className="font-mono">{selected.asset_code}</span> · {selected.asset_location || "—"} · Reported by{" "}
                  <b>{selected.reported_by_name}</b> · {formatAge(selected.reported_at)} ago
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-3 py-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase text-slate-500">Status</div>
                  <Badge variant="outline" className="mt-1">{selected.status}</Badge>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-slate-500">Assigned to</div>
                  <div className="mt-1">{selected.assigned_to_name || <span className="text-slate-400">Unassigned</span>}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-slate-500">Assign by (SLA)</div>
                  <div className="mt-1 text-xs">{selected.sla_assign_by ? new Date(selected.sla_assign_by).toLocaleString() : "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-slate-500">Resolve by (SLA)</div>
                  <div className="mt-1 text-xs">{selected.sla_resolve_by ? new Date(selected.sla_resolve_by).toLocaleString() : "—"}</div>
                </div>
              </div>

              <div className="mb-2">
                <div className="text-[11px] uppercase text-slate-500 mb-1">Issue reported</div>
                <p className="text-sm bg-slate-50 rounded p-3 border">{selected.description}</p>
              </div>

              {selected.voice_note_path && (
                <div className="mb-2" data-testid="kanban-voice-note-player">
                  <div className="text-[11px] uppercase text-slate-500 mb-1 flex items-center justify-between">
                    <span>Voice note{selected.voice_note_language ? ` · ${selected.voice_note_language}` : ""}</span>
                    {selected.voice_note_transcript && selected.voice_note_transcript !== selected.description && (
                      <span className="normal-case text-slate-400 italic truncate max-w-[60%]" title={selected.voice_note_transcript}>
                        {selected.voice_note_transcript}
                      </span>
                    )}
                  </div>
                  <audio
                    controls
                    className="w-full h-10"
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/maintenance-requests/${selected.id}/voice-note?auth=${localStorage.getItem("assetiq_token") || ""}`}
                  />
                </div>
              )}

              {(selected.photos || []).length > 0 && (
                <div className="mb-2">
                  <div className="text-[11px] uppercase text-slate-500 mb-1">Photos</div>
                  <div className="grid grid-cols-4 gap-2">
                    {(selected.photos || []).map((p, i) => (
                      <a key={i} href={p} target="_blank" rel="noreferrer">
                        <img src={p} alt="" className="rounded border w-full h-20 object-cover hover:opacity-80" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selected.status === "completed" && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm mt-2">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-1">
                    <CheckCircle2 className="w-4 h-4" /> Resolved
                  </div>
                  <div className="text-xs text-slate-700 space-y-1">
                    <div><b>Root cause:</b> {selected.root_cause_code}</div>
                    <div><b>Resolution:</b> {selected.resolution}</div>
                    <div><b>Downtime:</b> {selected.downtime_hours} hr · <b>Cost:</b> ₹{selected.cost}</div>
                  </div>
                </div>
              )}

              {selected.status === "cancelled" && (
                <div className="rounded-md bg-slate-100 border border-slate-200 p-3 text-sm mt-2">
                  <div className="flex items-center gap-2 text-slate-800 font-semibold mb-1">
                    <XCircle className="w-4 h-4" /> Cancelled
                  </div>
                  <div className="text-xs text-slate-700">{selected.cancel_reason}</div>
                </div>
              )}

              {/* Assign panel */}
              {selected.status === "requested" && canAssign && (
                <div className="border-t pt-4 mt-2 space-y-3">
                  <Label>Assign to technician</Label>
                  <Select value={assignTo} onValueChange={setAssignTo}>
                    <SelectTrigger data-testid="kanban-assign-select">
                      <SelectValue placeholder="Pick a technician" />
                    </SelectTrigger>
                    <SelectContent>
                      {technicians.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} <span className="text-slate-400 ml-1">· {t.role.replace("_", " ")}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Completion panel */}
              {canDoComplete && (
                <div className="border-t pt-4 mt-2 space-y-3">
                  <div>
                    <Label>Root cause</Label>
                    <Select value={rootCause} onValueChange={setRootCause}>
                      <SelectTrigger className="mt-1" data-testid="kanban-rootcause-select">
                        <SelectValue placeholder="Pick root cause" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROOT_CAUSES.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Resolution notes</Label>
                    <Textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} className="mt-1" data-testid="kanban-resolution-input" />
                  </div>
                  <div>
                    <Label>Repair cost (₹)</Label>
                    <Input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} className="mt-1" data-testid="kanban-cost-input" />
                  </div>
                </div>
              )}

              {/* Cancel panel */}
              {canCancel && ["requested", "assigned", "in_progress"].includes(selected.status) && (
                <div className="border-t pt-4 mt-2">
                  <Label>Cancel reason (optional)</Label>
                  <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Duplicate / false report" className="mt-1" data-testid="kanban-cancel-reason" />
                </div>
              )}

              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={closeDialog} data-testid="kanban-close-btn">Close</Button>
                {canCancel && ["requested", "assigned", "in_progress"].includes(selected.status) && (
                  <Button variant="outline" onClick={doCancel} disabled={actionLoading} data-testid="kanban-cancel-btn">
                    <XCircle className="w-4 h-4 mr-2" /> Cancel Request
                  </Button>
                )}
                {selected.status === "requested" && canAssign && (
                  <Button onClick={doAssign} disabled={actionLoading || !assignTo} data-testid="kanban-assign-btn">
                    <ArrowRight className="w-4 h-4 mr-2" /> Assign
                  </Button>
                )}
                {canStart && (
                  <Button onClick={doStart} disabled={actionLoading} data-testid="kanban-start-btn">
                    <PlayCircle className="w-4 h-4 mr-2" /> Start Work
                  </Button>
                )}
                {canDoComplete && (
                  <Button onClick={doComplete} disabled={actionLoading || !resolution.trim() || !rootCause} data-testid="kanban-complete-btn">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Complete
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
